import { useEffect, useMemo, useRef, useState, useCallback } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useSnackbar } from 'baseui/snackbar'
import { useTranslation } from 'react-i18next'
import { useParams, useNavigate } from 'react-router'

import { selectOrganizationsSelectedProvider } from '../organizationsManagementSelectors'
import { selectNettingUpdaterStoreData } from './nettingUpdaterSelectors'
import { setOrganizationId, setFile, clearFile, reset } from './nettingUpdaterSlice'
import { nettingUpdaterActions } from './nettingUpdaterSagas'

const useNettingUpdater = ({ isPro = false } = {}) => {
  const dispatch = useDispatch()
  const { enqueue } = useSnackbar()
  const { t } = useTranslation('organizationManagement')
  const navigate = useNavigate()
  const { providerUuid } = useParams()
  const fileRef = useRef(null)

  const selectedProvider = useSelector(selectOrganizationsSelectedProvider)
  const providerName = selectedProvider?.name || ''
  const providerId = selectedProvider?.id || null
  const acceptedExtensions = selectedProvider?.netting_accepted_extensions || []

  const [limit, setLimit] = useState('')

  // --- State PRO (local) ---
  const [proFileName, setProFileName] = useState(null)
  const [proIsSubmitting, setProIsSubmitting] = useState(false)
  const [proResultError, setProResultError] = useState(null)
  const [proResultMessage, setProResultMessage] = useState(null)

  // --- State Particuliers (Redux) ---
  const reduxData = useSelector(selectNettingUpdaterStoreData)

  const fileName = isPro ? proFileName : reduxData.fileName
  const isSubmitting = isPro ? proIsSubmitting : reduxData.isSubmitting
  const resultError = isPro ? proResultError : reduxData.resultError
  const resultMessage = isPro ? proResultMessage : reduxData.resultMessage

  useEffect(() => {
    if (!isPro) {
      dispatch(reset())
      if (providerId) {
        dispatch(setOrganizationId(providerId))
      }
    }
  }, [providerId, isPro])

  const isFormValid = useMemo(() => Boolean(providerId && fileName), [providerId, fileName])

  const onFileChange = e => {
    const file = e?.target?.files?.[0] || null
    fileRef.current = file
    if (isPro) {
      setProFileName(file?.name || null)
    } else {
      dispatch(setFile(file))
    }
  }

  const onFileClear = () => {
    fileRef.current = null
    if (isPro) {
      setProFileName(null)
    } else {
      dispatch(clearFile())
    }
  }

  const onSubmit = useCallback(() => {
    if (isPro) {
      dispatch({
        type: nettingUpdaterActions.SUBMIT,
        enqueue,
        t,
        file: fileRef.current,
        limit: limit ? Number(limit) : null,
        isPro: true,
        proCallbacks: {
          onRequest: () => {
            setProIsSubmitting(true)
            setProResultError(null)
            setProResultMessage(null)
          },
          onSuccess: message => {
            setProIsSubmitting(false)
            setProResultMessage(message)
            setProResultError(null)
          },
          onFailure: error => {
            setProIsSubmitting(false)
            setProResultError(error)
          },
        },
      })
    } else {
      dispatch({
        type: nettingUpdaterActions.SUBMIT,
        enqueue,
        t,
        file: fileRef.current,
        limit: limit ? Number(limit) : null,
      })
    }
  }, [isPro, enqueue, t, limit])

  const onGoBack = () => {
    navigate(-1)
  }

  const acceptedExtensionsString = useMemo(
    () => acceptedExtensions.map(ext => `.${ext}`).join(','),
    [acceptedExtensions],
  )

  return {
    form: {
      providerName,
      providerUuid,
      providerId,
      fileName,
      isSubmitting,
      isValid: isFormValid,
      acceptedExtensions: acceptedExtensionsString,

      limit,
      onLimitChange: e => setLimit(e.target.value.replace(/\D/g, '')),
      onFileChange,
      onFileClear,
      onSubmit,
      onGoBack,
    },
    result: {
      error: resultError,
      message: resultMessage,
    },
    t,
  }
}

export default useNettingUpdater
